import { inject, Injectable } from "@angular/core";
import { IgxGridComponent, ISortingExpression, SortingDirection } from "@infragistics/igniteui-angular";
import { CachedRepositoryService, NavigationService, StorageService } from "@tylertech/core-module";
import
{
    ODataApplyAggregate,
    ODataFilterBuilder,
    ODataFilterFactoryService,
} from "@tylertech/odata-module";
import { StringHelper, Utilities } from "@tylertech/shared-module";
import
{
    ClassMetadata,
    GridMetadata,
    IGridViewMetadata,
    IMetadataAction,
    IViewMetadata,
    KeyValuePair,
    MetadataSummary,
    PropertyMetadata,
    SelectionMode,
} from "@tylertech/tyler-api-types";
import { from, Observable, Subject, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { MetadataIntermediary, MetadataKey } from "../../interfaces";
import
{
    CachedCodeLookupService,
    ResetStateService,
    GridFilterService,
    GridMetadataService,
    GridSelectionService,
    InfoGridLayoutService,
    ITableFilter,
    MetadataEventService,
    MetadataFormService,
    MetadataInputSelectService,
    MetadataInputTypeHelperService,
    MetadataPropertyService,
    MetadataService,
    MetadataTableService,
    MetadataViewService,
    ProviderStorageService,
} from "../../services";
import { MetadataKeyHelpers, ObjectHelpers } from "../../utilities";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { recentSearchesKey } from "../../constants";

@Injectable({ providedIn: "root" })
export class GridLoadService extends ResetStateService
{
    private lookups: Record<string, MetadataKey[]> = {};

    private lookupCache: Record<string, KeyValuePair<MetadataKey, string>[]> = {};

    private documentsLoading: number = 0;

    private MetadataServerPaging: Record<string, boolean> = {};

    documentCountsLoaded: Record<string, boolean> = {}; //string is lookup provider type, bool is whether is loaded. infogrid should be able to see this to determine if we show a spinner

    SortingExpressions: Record<string, ISortingExpression[]> = {};

    InitialSortDone: Record<string, boolean> = {};

    Scrolling: Record<string, boolean> = {};

    FilterChanged: Record<string, boolean> = {};

    propertiesForCacheClear: MetadataIntermediary[] = [];

    constructor(
        private $odataFilterFactory: ODataFilterFactoryService,
        private $cachedCodeLookupService: CachedCodeLookupService,
        private $storageService: StorageService,
        private $metadataTableService: MetadataTableService,
        private $providerStorageService: ProviderStorageService,
        private $metadataService: MetadataService,
        private $metadataFormService: MetadataFormService,
        private $metadataPropertyService: MetadataPropertyService,
        private $metadataInputSelectService: MetadataInputSelectService,
        private $metadataInputTypeHelperService: MetadataInputTypeHelperService,
        private $repositoryService: CachedRepositoryService,
        private $metadataViewService: MetadataViewService,
        private $metadataEventService: MetadataEventService,
        private $gridMetadataService: GridMetadataService,
        private $gridFilterService: GridFilterService,
        private $gridLayoutService: InfoGridLayoutService,
        private $gridSelectionService: GridSelectionService
    )
    {
        super(inject(NavigationService));

        this.$metadataEventService.onLookupTypeCacheClear.pipe(takeUntilDestroyed())
            .subscribe((type) =>
            {
                const typeMatch = this.propertiesForCacheClear.find(p => p.property.Lookup === type);

                let typeToCheck;
                if (type === "UserDefinedData" || type === "DocumentTypeName")
                    typeToCheck = type;
                else if (typeMatch)
                    typeToCheck = typeMatch.property.Lookup;

                if (typeToCheck)
                {
                    for (const key in this.lookupCache)
                    {
                        if (key.indexOf(typeToCheck) > -1)
                            this.lookupCache[key] = undefined;
                    }
                }
            });
    }

    public getData(
        grid: IgxGridComponent,
        model: any,
        viewMetadata: IGridViewMetadata,
        metadataServerPaging: boolean,
        applyPaging: boolean,
        afterFetch: (applyPaging: boolean) => void,
        skip?: number,
        take?: number,
        skipSort?: boolean,
        loadRecent?: boolean
    ): Subscription
    {
        const providerId = viewMetadata.GridProperties["$providerId"];
        this.$gridLayoutService.GridLayoutId[providerId] = viewMetadata.GridId;
        if (!this.InitialSortDone[providerId] && !skipSort)
            this.SetInitialSort(providerId, viewMetadata, grid);
        if (skipSort)
            grid.sortingExpressions = [];
        if (!this.SortingExpressions[providerId] || grid.sortingExpressions?.length > 0)
            this.SortingExpressions[providerId] = grid.sortingExpressions;
        this.MetadataServerPaging[providerId] = metadataServerPaging;
        return from(this.MetadataFetch(grid, providerId, model, viewMetadata, metadataServerPaging, skip, take, loadRecent))
            .pipe(
                map(() =>
                {
                    applyPaging = false;
                })
            )
            .subscribe(() =>
            {
                if (afterFetch) afterFetch(applyPaging);
            });
    }

    GetTotalRows(providerId: string): number
    {
        return this.$providerStorageService.TotalHits[providerId];
    }

    MetadataFetch(
        grid: IgxGridComponent,
        providerId: string,
        model: any,
        viewMetadata: IGridViewMetadata,
        server: boolean,
        skip: number,
        take: number,
        loadRecent: boolean
    ): Promise<any>
    {
        const self = this;
        let forceRefetch = false;
        if (this.$providerStorageService.Refetch[providerId])
        {
            forceRefetch = this.$providerStorageService.Refetch[providerId] === true;
            this.$providerStorageService.Refetch[providerId] = false;
        }
        const recentIds = (loadRecent && this.allowShowRecent(viewMetadata)) ? this.recentIds(viewMetadata.GridId) : undefined;
        const tableFilter = <ITableFilter>{
            Top: take,
            OrderBy: this.FetchOrder(providerId),
            Skip: skip,
            Search: grid.filteringExpressionsTree.filteringOperands,
            Properties: viewMetadata.GridProperties.ClassMetadata.Properties,
            GroupedColumns: grid.groupingExpressions?.map(g => g.fieldName)
        };

        if (server)
            this.$providerStorageService.GridData[providerId] = undefined;

        if (!viewMetadata.SubPropertyName && (server || this.$metadataViewService.IsProjectionGrid(viewMetadata)))
        {
            if (server || forceRefetch || !this.$providerStorageService.GridData[providerId])
            {
                const odataFilterBuilder = this.oDataFilterBuilder(providerId, viewMetadata, skip, take, server);
                return this.$gridFilterService.buildOdataFilter(grid.filteringExpressionsTree, viewMetadata.GridProperties.ClassMetadata.Properties, recentIds, viewMetadata.GridProperties.IdProperty)
                    .then(filter =>
                    {
                        if (filter)
                            odataFilterBuilder.constantFilter(filter.replace("$filter=", ""));
                        return this.$repositoryService
                            .Fetch(viewMetadata.GridProperties.ClassMetadata.TypeName, odataFilterBuilder.build())
                            .then(result =>
                            {
                                if (self.$metadataViewService.IsProjectionGrid(viewMetadata))
                                    return augmentAndLoad(result["value"]);
                                return self.BuildGridRows(result["value"], viewMetadata, server, null)
                                    .then(rows =>
                                    {
                                        self.$providerStorageService.GridData[providerId] = rows;
                                        self.$providerStorageService.TotalHits[providerId] = result["@odata.count"];
                                        self.syncPaged(providerId, viewMetadata);
                                    });
                            });
                    });
            }
            else if (!server && this.$metadataViewService.IsProjectionGrid(viewMetadata))
                //after initial load, a non-server paged projection grid should be treated just like a subproperty grid that is fully loaded locally
                return this.localDataGridLoad(grid, tableFilter, providerId, viewMetadata);

        }
        else if (!viewMetadata.SubPropertyName && forceRefetch)
        {
            // This is not projection, server paging, subproperty grid with cached data cleared, so need to refetch data
            const odataFilter = this.$metadataViewService.createFilterString(model, viewMetadata);
            return this.$gridFilterService.buildOdataFilter(grid.filteringExpressionsTree, viewMetadata.GridProperties.ClassMetadata.Properties, recentIds, viewMetadata.GridProperties.IdProperty)
                .then(filter =>
                {
                    return self.$repositoryService.Fetch(viewMetadata.GridProperties.ClassMetadata.TypeName, combineFilter(odataFilter, filter))
                        .then(result => augmentAndLoad(result));
                });
        }
        return self.localDataGridLoad(grid, tableFilter, providerId, viewMetadata);

        function combineFilter(odataFilter: string, filter: string): string
        {
            if (Utilities.IsNullOrUndefined(odataFilter))
                odataFilter = "";
            if (Utilities.IsNullOrUndefined(filter))
                filter = "";
            let combinedFilter = odataFilter + filter;
            if (combinedFilter === "")
                return null;
            return combinedFilter;
        }

        function augmentAndLoad(entities: any[]): Promise<any>
        {
            return self.$metadataTableService.AugmentModel(viewMetadata.GridProperties.ClassMetadata, entities, viewMetadata.SelectProperty ? viewMetadata.SelectProperty : "Selected")
                .then(() =>
                {
                    self.$providerStorageService.LocalEntitiesStore[providerId] = entities;
                    self.$providerStorageService.FilteredEntities[providerId] = [];
                    return self.localDataGridLoad(grid, tableFilter, providerId, viewMetadata);
                });
        }
    }

    localDataGridLoad(grid: IgxGridComponent, tableFilter: ITableFilter, providerId: string, viewMetadata: IGridViewMetadata): Promise<any>
    {
        const self = this;
        return self.ApplyFilterSort(grid, tableFilter, providerId, viewMetadata)
            .then(filteredEntities =>
            {
                if (self.$providerStorageService.IsInfiniteScroll(providerId))
                    self.$providerStorageService.TotalHits[providerId] = filteredEntities.filter(x => x.SerializedRowState !== "Deleted").length;
                const pagedEntities = self.$metadataTableService.GetPagedEntities(providerId, tableFilter, filteredEntities);
                return self.maybeFetchDocuments(viewMetadata, tableFilter, pagedEntities, grid)
                    .then(modEntities =>
                    {
                        return self.BuildGridRows(modEntities, viewMetadata, false, viewMetadata.Actions)
                            .then(rows =>
                            {
                                const documentLookupProperty = viewMetadata?.GridProperties?.ClassMetadata?.Properties.filter(x => !Utilities.IsNullOrUndefinedOrEmpty(x.Lookup) && x.Lookup.startsWith("DocumentTypeName"))[0];
                                if (documentLookupProperty)
                                {
                                    //this post lookup is done for the case where we have paged data. It needs to be done afterwards, otherwise we would be modifying the localEntitiesStore.
                                    Promise.resolve(self.loadDocumentCounts(rows, documentLookupProperty));
                                }
                            })
                            .then(_ => buildColumnSummaries());
                    });
            });

        function buildColumnSummaries(): void
        {
            if (!self.$providerStorageService.BuildSummaries[providerId])
                return;
            self.$providerStorageService.ColumnSummaries[providerId] = {};
            self.SetColumnSummaries(providerId, viewMetadata);
            self.$providerStorageService.BuildSummaries[providerId] = false;
        }
    }

    hasSummary(classMetadata: ClassMetadata): boolean
    {
        let hasSummary = false;
        classMetadata.Properties.forEach(property =>
        {
            if (property.ClassMetadata && this.hasSummary(property.ClassMetadata)) hasSummary = true;
            if (property.SummaryType === MetadataSummary.Sum) hasSummary = true;
        });
        return hasSummary;
    }

    oDataFilterBuilder(
        providerId: string,
        viewMetadata: IGridViewMetadata,
        skip: number,
        take: number,
        server: boolean
    ): ODataFilterBuilder
    {
        const self = this;
        const odataFilterBuilder = this.$odataFilterFactory.createBuilder();
        this.applyGridFiltering(odataFilterBuilder, viewMetadata);
        const props = this.getSelects(viewMetadata.GridProperties.ClassMetadata);
        const propsWithId = [...props];
        this.pushProps(viewMetadata.GridProperties.IdProperty, propsWithId);
        this.pushProps(viewMetadata.GridProperties.CanEditProperty, propsWithId);
        this.pushProps(viewMetadata.GridProperties.CanDeleteProperty, propsWithId);
        this.pushProps(viewMetadata.GridProperties.CanCopyProperty, propsWithId);
        viewMetadata.GridProperties.ClassMetadata.Properties.forEach(p =>
        {
            this.pushProps(p.HiddenProperty, propsWithId);
            self.pushProps(p.DisabledProperty, propsWithId);
            if (p.ActionMetadata)
            {
                self.pushProps(p.ActionMetadata.HiddenProperty, propsWithId);
                self.pushProps(p.ActionMetadata.DisabledProperty, propsWithId);
            }
        });
        if (!Utilities.IsNullOrUndefined(viewMetadata.GridProperties.Actions))
        {
            viewMetadata.GridProperties.Actions.forEach(a =>
            {
                if (a.Icon && a.Icon.indexOf("{") === 0)
                {
                    this.pushProps(a.Icon.substring(1, a.Icon.length - 1), propsWithId);
                }
                if (a.Label && a.Label.indexOf("{") === 0)
                {
                    this.pushProps(a.Label.substring(1, a.Label.length - 1), propsWithId);
                }
                this.pushProps(a.HiddenProperty, propsWithId);
            });
        }
        // Without sort properties from select, the grid may not be able to sort properly after Edits
        if (!Utilities.IsNullOrUndefined(viewMetadata.GridProperties.Sort))
        {
            this.pushProps(viewMetadata.GridProperties.Sort.Name, propsWithId);
        }
        if (!Utilities.IsNullOrUndefined(viewMetadata.GridProperties.SecondarySort))
        {
            this.pushProps(viewMetadata.GridProperties.SecondarySort.Name, propsWithId);
        }
        if (
            !Utilities.IsNullOrUndefined(viewMetadata.GridProperties.FilterMetadata) &&
            !Utilities.IsNullOrUndefined(viewMetadata.GridProperties.FilterMetadata.Properties)
        )
        {
            this.getSelects(viewMetadata.GridProperties.FilterMetadata)
                .forEach(p =>
                {
                    this.pushProps(p, propsWithId);
                });
        }
        // We may need to check other things that are depended on model properties
        odataFilterBuilder.select(propsWithId)
            .count();
        if (
            !Utilities.IsNullOrUndefined(this.SortingExpressions[providerId]) &&
            this.SortingExpressions[providerId].length > 0
        )
        {
            const expressions: string[] = [];
            this.SortingExpressions[providerId].forEach(e =>
            {
                if (!Utilities.IsNullOrUndefinedOrEmpty(e.fieldName))
                    expressions.push(`${e.fieldName} ${e.dir === SortingDirection.Desc ? "DESC" : "ASC"}`);
            });
            if (expressions.length > 0) odataFilterBuilder.orderby(expressions.join(","));
        }
        if (server)
        {
            if (take) odataFilterBuilder.top(take);
            if (skip) odataFilterBuilder.skip(skip);
        }
        return odataFilterBuilder;
    }

    applyGridFiltering(odataFilterBuilder: ODataFilterBuilder, viewMetadata: IGridViewMetadata): void
    {
        // apply grid filters if available, or use the original constant filter
        if (
            !Utilities.IsNullOrUndefined(viewMetadata.GridProperties.FilterMetadata) &&
            !Utilities.IsNullOrUndefined(viewMetadata.GridProperties["$$filterModel"])
        )
        {
            for (const p in viewMetadata.GridProperties["$$filterModel"])
            {
                if (
                    !p.startsWith("$") &&
                    viewMetadata.GridProperties.FilterMetadata.Properties.filter(x => x.Name === p).length > 0
                )
                {
                    odataFilterBuilder.filterEQ(p, viewMetadata.GridProperties["$$filterModel"][p]);
                }
            }
        }
        else if (!Utilities.IsNullOrUndefinedOrEmpty(viewMetadata.ConstantFilter))
        {
            odataFilterBuilder.constantFilter(viewMetadata.ConstantFilter);
        }
    }

    UpdatedEntities: Record<string, any[]> = {};

    updateEntities(providerId: string, entities: any[]): void
    {
        if (Utilities.IsNullOrUndefined(this.UpdatedEntities[providerId])) this.UpdatedEntities[providerId] = [];
        this.UpdatedEntities[providerId].push(...entities);
    }

    syncPaged(providerId: string, viewMetadata: IGridViewMetadata): void
    {
        const self = this;
        if (
            !viewMetadata.GridProperties.IdProperty ||
            !this.UpdatedEntities[providerId]
        )
            return;
        this.UpdatedEntities[providerId].forEach(entity =>
        {
            const row = self.$providerStorageService.GridData[providerId].filter(
                x => x.Id === entity[viewMetadata.GridProperties.IdProperty]
            )[0];
            if (!!row && entity.SerializedRowState === "Deleted")
            {
                const index = self.$providerStorageService.GridData[providerId].indexOf(row);
                if (index > -1) self.$providerStorageService.GridData[providerId].splice(index, 1);
                self.$providerStorageService.TotalHits[providerId]--;
            }
            else if (entity.SerializedRowState === "Added")
            {
                const index = this.$providerStorageService.GridData[providerId].length;
                const row = this.createDataRow(
                    entity,
                    index,
                    viewMetadata,
                    null
                );
                if (this.$providerStorageService.GridData[providerId].length > 0)
                    this.$providerStorageService.GridData[providerId][0] = row;
                else this.$providerStorageService.GridData[providerId].push(row);
                self.$providerStorageService.TotalHits[providerId]++;
            }
            else if (row)
            {
                viewMetadata["$columns"].forEach(column =>
                {
                    if (entity[column.Name] !== undefined)
                        row[column.Name] = self.$metadataService.getFormattedValueFromProp(
                            entity[column.Name],
                            self.$gridMetadataService.getProperty(
                                viewMetadata.GridProperties.ClassMetadata,
                                column.Name
                            )
                        );
                });
            }
        });
        this.fireModelChangedListeners(providerId);
    }

    UpdateProjectionTableEntity(providerId: string, entities: any[], grid: IgxGridComponent, viewMetadata: IGridViewMetadata, page: number, perPage: number): Promise<void>
    {
        this.updateEntities(providerId, entities);
        const idProperty = viewMetadata.GridProperties.IdProperty;
        const entity = entities[0];
        const doLocalMerge: boolean = !viewMetadata.EditForm.RecalculateParentFormOnSave && !this.MetadataServerPaging[providerId];
        const promises: Promise<void>[] = [];

        if (entity.SerializedRowState === "Deleted")
        {
            const toDeleteRows = this.$providerStorageService.GridData[providerId].filter(x => x["$$Deleting"]);
            const toDelete = toDeleteRows.length >= 1 ? toDeleteRows[0] : undefined;

            if (!Utilities.IsNullOrUndefined(toDelete) && !Utilities.IsNullOrUndefined(toDelete.Id))
            {
                if (this.MetadataServerPaging[providerId])
                {
                    const index = this.$providerStorageService.GridData[providerId].indexOf(toDelete);
                    this.$providerStorageService.GridData[providerId].splice(index, 1);
                    this.$providerStorageService.TotalHits[providerId]--;
                }
                else
                {
                    //if not server paged, we need to splice from LocalEntitiesStore, then re-page to get the GridData
                    toDeleteRows.forEach(x => this.removeDeleted(x, idProperty, providerId));
                    this.$providerStorageService.GridData[providerId] = undefined;
                    this.$providerStorageService.FilteredEntities[providerId] = undefined;
                    const tableFilter = <ITableFilter>{
                        Top: perPage,
                        OrderBy: this.FetchOrder(providerId),
                        Skip: page * perPage,
                        Search: grid.filteringExpressionsTree.filteringOperands,
                        Properties: viewMetadata.GridProperties.ClassMetadata.Properties,
                    };
                    promises.push(this.localDataGridLoad(grid, tableFilter, providerId, viewMetadata));
                }
            }
        }
        else if (entity.SerializedRowState === "Added")
        {
            if (doLocalMerge)
            {
                let entityToAdd = { SerializedRowState: "Added", $$TypeName: viewMetadata.GridProperties.ClassMetadata.TypeName};
                promises.push(...this.updateRowModel(viewMetadata, entity, entityToAdd));
                this.$providerStorageService.LocalEntitiesStore[providerId] = this.$providerStorageService.LocalEntitiesStore[providerId] ?? [];
                this.$providerStorageService.LocalEntitiesStore[providerId].push(entityToAdd);
            }
        }
        else
        {
            const row = this.$providerStorageService.GridData[providerId]?.find(x => x["$$model"] && x["$$model"]["$$Editing"]);
            if (row && doLocalMerge)
            {
                const rowModel = row["$$model"];
                promises.push(...this.updateRowModel(viewMetadata, entity, rowModel));
                delete row["$$model"]["$$Editing"];
            }
        }
        this.fireModelChangedListeners(providerId);
        delete viewMetadata["$projectionGridUpdatingEntity"];
        return Promise.all(promises).then(() =>
        {
            return;
        });
    }

    updateRowModel(viewMetadata: IGridViewMetadata, entity: any, rowModel: any): Promise<void>[]
    {
        const promises: Promise<void>[] = [];
        viewMetadata["$columns"].forEach(column =>
        {
            let entityField;
            if (viewMetadata.GridProperties["FieldMap"] && viewMetadata.GridProperties["FieldMap"][column.Name])
                entityField = viewMetadata.GridProperties["FieldMap"][column.Name];
            else if (entity[column.Name] !== undefined)
                entityField = column.Name;

            if (entityField)
            {
                let propertyMetadata: PropertyMetadata;
                viewMetadata.EditForm.Views.forEach(v =>
                {
                    if (!propertyMetadata)
                        propertyMetadata = this.getPropertyMetadataFromEditForm(v, entityField);
                });
                if (!propertyMetadata)
                    propertyMetadata = this.$gridMetadataService.getProperty(viewMetadata.GridProperties.ClassMetadata, entityField);
                if (propertyMetadata)
                {
                    if (this.$metadataInputTypeHelperService.isLookup(propertyMetadata.InputType))
                    {
                        const builder = this.$odataFilterFactory.createBuilder();
                        builder.filterEQ("Key", entity[entityField]);
                        promises.push(this.$cachedCodeLookupService.Get(propertyMetadata.Lookup, builder.build())
                            .then(x =>
                                {
                                    if (x.length === 1)
                                        rowModel[column.Name] = x[0].Value;
                                }));
                    }
                    else
                        rowModel[column.Name] = this.$metadataService.getFormattedValueFromProp(entity[entityField], propertyMetadata);
                }
            }
        });
        return promises;
    }

    getPropertyMetadataFromEditForm(viewMetadata: IViewMetadata, property: string): PropertyMetadata
    {
        let propertyMetadata: PropertyMetadata;
        if (this.$metadataViewService.IsCombinedView(viewMetadata))
        {
            viewMetadata.Views.forEach(v =>
            {
                if (!propertyMetadata)
                    propertyMetadata = this.getPropertyMetadataFromEditForm(v, property);
                else
                    return propertyMetadata;
            });
        }
        else if (viewMetadata?.ViewMetaData?.Properties)
            propertyMetadata = viewMetadata.ViewMetaData.Properties.find(p => p.Name === property);
        return propertyMetadata;
    }

    removeDeleted(toDelete: any, idProperty: string, providerId: string): void
    {
        const modelToDelete = toDelete["$$model"];
        const existingRow = this.$providerStorageService.LocalEntitiesStore[providerId].find(
            row => row[idProperty] === modelToDelete[idProperty]
        );
        //TODO: this is some weird casing of finding a record due to potential lack of an Id...need to shore this up
        let index = this.$providerStorageService.LocalEntitiesStore[providerId].indexOf(existingRow);
        if (index >= 0)
            this.$providerStorageService.LocalEntitiesStore[providerId].splice(index, 1);
        index = this.$providerStorageService.LocalEntitiesStore[providerId].indexOf(toDelete);
        if (index >= 0)
            this.$providerStorageService.LocalEntitiesStore[providerId].splice(index, 1);
        index = this.$providerStorageService.LocalEntitiesStore[providerId].indexOf(
            this.$providerStorageService.LocalEntitiesStore[providerId].find(
                x => this.$metadataTableService.GetId(idProperty, x, 0) === toDelete.Id
            )
        );
        if (index >= 0)
            this.$providerStorageService.LocalEntitiesStore[providerId].splice(index, 1);
        if (toDelete["$$model"]["IsAddMode"] === true)
        {
            const index = this.$providerStorageService.LocalEntitiesStore[providerId].indexOf(toDelete);
            if (index >= 0)
                this.$providerStorageService.LocalEntitiesStore[providerId].splice(index, 1);
        }
    }

    pushProps(property: string, props: any[]): void
    {
        if (!!property && props.indexOf(property) === -1) props.push(property);
    }

    FetchOrder(providerId: string): string
    {
        if (!Utilities.IsNullOrUndefined(this.SortingExpressions[providerId]))
        {
            const expressions: string[] = [];
            this.SortingExpressions[providerId].forEach(e =>
            {
                expressions.push(`${e.fieldName} ${e.dir === SortingDirection.Desc ? "DESC" : "ASC"}`);
            });
            return expressions.join(",");
        }
        return null;
    }

    getSelects(metadata: ClassMetadata, parent: string = ""): any[]
    {
        let props: any[] = [];
        metadata.Properties.forEach(p =>
        {
            if (p.ClassMetadata) props = props.concat(this.getSelects(p.ClassMetadata, `${p.Name}/`));
            else props.push(`${parent}${p.Name}`);
        });
        return props;
    }

    isOrderbyChildOrLookup(orderby: string, metadata: ClassMetadata): boolean
    {
        if (!orderby)
            return false;
        const columnSort = orderby.split(",");
        return columnSort.some(x => this.isOrderbyChildOrLookupImpl(x, metadata));
    }

    isOrderbyChildOrLookupImpl(orderby: string, metadata: ClassMetadata): boolean
    {
        const o = orderby.split(" ");
        orderby = o[0];
        const property = metadata.Properties.find(x => x.Name === orderby);
        if (property)
        {
            return (
                !Utilities.IsNullOrUndefined(property.Lookup) ||
                !Utilities.IsNullOrUndefined(property.FromColumnId)
            );
        }
        let ret = false;
        metadata.Properties.filter(x => x.ClassMetadata)
            .forEach(e =>
            {
                if (!Utilities.IsNullOrUndefined(e.ClassMetadata.Properties.filter(x => x.Name === orderby).length > 0))
                {
                    ret = true;
                }
            });
        return ret;
    }

    maybeFetchDocuments(viewMetadata: IGridViewMetadata, tableFilter: ITableFilter, entities: any[], grid: IgxGridComponent): Promise<any[]>
    {
        const documentLookupProperty = viewMetadata?.GridProperties?.ClassMetadata?.Properties.filter(x => !Utilities.IsNullOrUndefinedOrEmpty(x.Lookup) && x.Lookup?.startsWith("DocumentTypeName"))[0];
        const providerId = viewMetadata.GridProperties["$providerId"];

        //only do something special here if we are sorting or filtering by a doc count column.
        if (tableFilter.OrderBy.includes(documentLookupProperty?.Name) || tableFilter.Search?.some(x => x.fieldName === documentLookupProperty?.Name))
        {
            //load all, attempt sort again after getting doc counts
            return Promise.resolve(this.loadDocumentCounts(this.$providerStorageService.LocalEntitiesStore[providerId], documentLookupProperty).then(rows =>
            {
                if (!Utilities.IsNullOrUndefined(rows))
                {
                    const data = this.$gridFilterService.filterLocalTable(rows, grid);
                    rows = this.sort(providerId, data, viewMetadata)
                        .map(x => x["$$model"]);
                }

                rows = this.$metadataTableService.GetPagedEntities(providerId, tableFilter, rows);
                return rows;
            }));
        }

        return Promise.resolve(entities);

    }

    loadDocumentCounts(entities: any[], documentLookupProperty: PropertyMetadata): Promise<any[]>
    {
        if (!entities || entities.length === 0)
            return Promise.resolve(entities);

        const self = this;
        //we need to do this in the case where pagedEntities in loadpagedtable becomes the actual model
        self.documentCountsLoaded[documentLookupProperty.Lookup.split(':')[1]] = false;
        self.documentsLoading++;
        return self.LookupValuesForTable(documentLookupProperty, entities.filter(x => Utilities.isGuid(x[documentLookupProperty.Name])), getEntitiesFromPageOrLookup(entities, documentLookupProperty.Name)).then(() =>
        {
            self.documentsLoading--;
            if (self.documentsLoading === 0)
                self.documentCountsLoaded[documentLookupProperty.Lookup.split(':')[1]] = true;
            return entities;
        });

        function getEntitiesFromPageOrLookup(ents: any, docLookupName: string): any[]
        {
            if (!self.lookups[docLookupName] || ents.every(x => x[docLookupName] === 'Error Loading'))
                return ents.filter(x => Utilities.isGuid(x[docLookupName])).map(x => x[docLookupName]);

            const idsInLookupCache = ents.map(x => self.lookups[docLookupName].filter(z => z === x[docLookupName])[0]).filter(filteredLookups => Utilities.isGuid(filteredLookups));
            return idsInLookupCache.length === 0 ? self.lookups[docLookupName].filter(x => Utilities.isGuid(x)) : idsInLookupCache;
        }
    }

    sort(providerId: string, rows: any[], viewMetadata: IGridViewMetadata): any[]
    {
        const self = this;
        if (!Utilities.IsNullOrUndefined(this.SortingExpressions[providerId]) && !this.isAddingOrEditing(viewMetadata) && rows?.length > 0)
        {
            this.SortingExpressions[providerId]
                .slice()
                .reverse()
                .forEach(e =>
                {
                    const fieldName = e.fieldName in rows[0] ? e.fieldName : (viewMetadata.GridProperties.IdProperty ?? "Id");
                    const property = viewMetadata.GridProperties.ClassMetadata.Properties.find(x => x.Name === fieldName);
                    const isDate = property
                        && (property.InputType === "Date"
                            || property.InputType === "DateTime"
                            || property.InputType === "Time");

                    rows.sort((a, b) =>
                    {
                        const ret = self._sort(a, b, fieldName, e.dir === SortingDirection.Desc ? "DESC" : "ASC", isDate);
                        return ret;
                    });
                });
        }
        return rows;
    }

    _sort(a: any, b: any, field: string, sortDirection: string, isDate: boolean): number
    {
        let ret = this.compare(a, b, field, isDate);
        if (sortDirection === "DESC")
        {
            ret *= -1;
        }
        return ret;
    }

    compare(a: any, b: any, field: string, isDate: boolean): number
    {
        const aVal = this.getValue(a, field);
        const bVal = this.getValue(b, field);
        for (let i: number = 0; i < aVal.length; i++)
        {
            if (typeof aVal[i] === typeof bVal[i] && aVal[i] !== bVal[i])
            {
                if (isDate)
                    return new Date(aVal[1]).getTime() - new Date(bVal[1]).getTime() > 0 ? 1 : -1;
                return aVal[i] > bVal[i] ? 1 : -1;
            }
        }
        return 0;
    }

    private isAddingOrEditing(viewMetadata: IGridViewMetadata): boolean
    {
        return viewMetadata["$$cellEditing"] || viewMetadata["$$cellAdding"];
    }

    SetInitialSort(providerId: string, metadata: IGridViewMetadata, grid: IgxGridComponent): void
    {
        if (
            Utilities.IsNullOrUndefined(metadata.GridProperties.Sort) ||
            metadata.GridProperties.Sort.Name === "Sort_IgnoreSort" ||
            grid.sortingExpressions?.length > 0
        )
        {
            this.InitialSortDone[providerId] = true;
            if (!this.SortingExpressions[providerId] || grid.sortingExpressions?.length > 0)
                this.SortingExpressions[providerId] = grid.sortingExpressions;
            return;
        }
        this.SortingExpressions[providerId] = [];
        let values = metadata.GridProperties.Sort.Name.split(".");
        this.SortingExpressions[providerId].push(<ISortingExpression>{
            fieldName: values[values.length - 1],
            dir: metadata.GridProperties.Sort.Direction === "DESC" ? 2 : 1,
            ignoreCase: true,
        });
        if (metadata.GridProperties.SecondarySort)
        {
            values = metadata.GridProperties.SecondarySort.Name.split(".");
            this.SortingExpressions[providerId].push(<ISortingExpression>{
                fieldName: values[values.length - 1],
                dir: metadata.GridProperties.SecondarySort.Direction === "DESC" ? 2 : 1,
                ignoreCase: true,
            });
        }
        this.InitialSortDone[providerId] = true;
        grid.sortingExpressions = this.SortingExpressions[providerId];
    }

    getValue(row: any, field: string): any[]
    {
        const cell = row[field];
        if (Utilities.IsNullOrUndefined(cell))
        {
            return [0, cell, cell];
        }
        let value: string;
        if (ObjectHelpers.isNumber(cell))
            value = StringHelper.parseNumber(cell.toString()).toString();
        else
            value = cell;

        if (!Number.isNaN(Number(value)))
        {
            return [0, Number(value), cell.toString()];
        }
        if (typeof value === "string")
        {
            return [0, value.toLowerCase(), cell.toString()
                .toLowerCase()];
        }
        return [1, value, cell.toString()
            .toLowerCase()];
    }

    SetColumnSummaries(
        providerId: string,
        viewMetadata: IGridViewMetadata,
    ): void
    {
        let data = this.$providerStorageService.LocalEntitiesStore[providerId].filter(x => x["$inFilter"]);
        const summaryColumns = this.$gridMetadataService.getSummaryColumns(viewMetadata.GridProperties.ClassMetadata);
        if (summaryColumns.length === 0)
            return;
        const summaries = {};
        if (data)
            data.forEach(row =>
            {
                if (row.SerializedRowState !== "Deleted")
                {
                    let subPropertyEntity = row;
                    summaryColumns.forEach(name =>
                    {
                        const chain = name.split(".");
                        let childSubPropertyEntity = subPropertyEntity;
                        if (!summaries[chain[chain.length - 1]])
                            summaries[chain[chain.length - 1]] = 0;
                        for (let i = 0; i < chain.length - 1; i++)
                        {
                            const element = chain[i];
                            childSubPropertyEntity = childSubPropertyEntity[element];
                        }
                        const value = childSubPropertyEntity[chain[chain.length - 1]] ?? "";
                        summaries[chain[chain.length - 1]] += Number(value.toString()
                            .replace(/[^0-9.-]+/g, ""));
                    });
                }
            });
        this.$providerStorageService.ColumnSummaries[providerId] = summaries;
    }

    GetColumnSummaries(
        providerId: string,
        grid: IgxGridComponent,
        viewMetadata: IGridViewMetadata,
        metadataServerPaging: boolean,
        summaryColumns: string[],
        loadRecent: boolean
    ): Promise<Record<string, number>>
    {
        if (metadataServerPaging)
            return this.getODataSummaries(grid, viewMetadata, summaryColumns, loadRecent, providerId);
        if (!Utilities.IsNullOrUndefinedOrEmptyObject(this.$providerStorageService.ColumnSummaries[providerId]))
            return Promise.resolve(this.$providerStorageService.ColumnSummaries[providerId]);
        return Promise.resolve({});
    }

    getODataSummaries(
        grid: IgxGridComponent,
        viewMetadata: IGridViewMetadata,
        summaryColumns: string[],
        loadRecent: boolean,
        providerId: string
    ): Promise<Record<string, number>>
    {
        const self = this;
        const recentIds = (loadRecent && this.allowShowRecent(viewMetadata)) ? this.recentIds(viewMetadata.GridId) : undefined;
        const odataFilterBuilder = this.$odataFilterFactory.createBuilder();
        this.applyGridFiltering(odataFilterBuilder, viewMetadata);
        const aggregates: ODataApplyAggregate[] = [];
        summaryColumns.forEach(column =>
        {
            aggregates.push({
                field: column.replace(/\./g, "/"),
                type: "sum",
                alias: `${column.substring(column.lastIndexOf(".") + 1)}Total`
            });
        });
        odataFilterBuilder.apply(aggregates);
        return this.$gridFilterService.buildOdataFilter(grid.filteringExpressionsTree, viewMetadata.GridProperties.ClassMetadata.Properties, recentIds, viewMetadata.GridProperties.IdProperty)
            .then(filter =>
            {
                return this.$repositoryService
                    .Fetch(viewMetadata.GridProperties.ClassMetadata.TypeName, odataFilterBuilder.build() + filter)
                    .then(result =>
                    {
                        if (result && result["value"] && result["value"].length === 1 && result["value"][0])
                        {
                            const summaries = {};
                            Object.keys(result["value"][0])
                                .forEach(key =>
                                {
                                    if (key !== "$$TypeName")
                                        summaries["Number"] = result["value"][0][key];
                                });
                            self.$providerStorageService.ColumnSummaries[providerId] = summaries;
                            return summaries;
                        }
                        return {};
                    });
            });
    }


    ApplyFilterSort(grid: IgxGridComponent, tableFilter: ITableFilter, providerId: string, viewMetadata: IGridViewMetadata): Promise<any[]>
    {
        const self = this;
        let includeLookups: string[] = [];
        let hasFilter = false;
        let filteredColumns: string[];

        if ((tableFilter.Search && tableFilter.Search.length > 0) || tableFilter.OrderBy || tableFilter.GroupedColumns?.length > 0)
        {
            const filteredEntities = self.$providerStorageService.FilteredEntities[providerId];
            if (!this.FilterChanged[providerId] && filteredEntities?.length > 0)
                return Promise.resolve(filteredEntities);
            hasFilter = true;
            filteredColumns = tableFilter.Search.map(expressionTree => expressionTree.fieldName);
            if (tableFilter.OrderBy)
            {
                const sorts = tableFilter.OrderBy.split(",");
                filteredColumns.push(...sorts.map(x => x.split(" ")[0]));
            }
            if (tableFilter.GroupedColumns?.length > 0)
                filteredColumns.push(...tableFilter.GroupedColumns);
            const intermediaries = this.$metadataService.FlattenProperties(viewMetadata.GridProperties.ClassMetadata, null, {});
            includeLookups = intermediaries.filter(p => this.$metadataInputTypeHelperService.isLookup(p.property.InputType)).map(x => x.name);

            filteredColumns = updateParent(filteredColumns, intermediaries);
            includeLookups = updateParent(includeLookups, intermediaries);
        }

        this.FilterChanged[providerId] = false;

        if (!hasFilter)
        {
            self.$providerStorageService.LocalEntitiesStore[providerId].forEach(cachedEntity => cachedEntity["$inFilter"] = true);
            self.$providerStorageService.FilteredEntities[providerId] = self.$providerStorageService.LocalEntitiesStore[providerId];
            return Promise.resolve(self.$providerStorageService.LocalEntitiesStore[providerId]);
        }

        return this.BuildGridRows(this.$providerStorageService.LocalEntitiesStore[providerId], viewMetadata, false, viewMetadata.Actions, filteredColumns, includeLookups, hasFilter)
            .then(rows =>
            {
                const sortedRows = self.sort(providerId, rows, viewMetadata);
                const sortedEntities = sortedRows.map(x => x["$$model"]);
                this.$providerStorageService.LocalEntitiesStore[providerId].sort((a, b) => sortedEntities.indexOf(a) - sortedEntities.indexOf(b));

                let filteredRows = self.$gridFilterService.filterLocalTable(rows, grid);
                const filteredEntities = self.sort(providerId, filteredRows, viewMetadata).map(x => x["$$model"]);
                self.$providerStorageService.LocalEntitiesStore[providerId].forEach(cachedEntity => cachedEntity["$inFilter"] = false);
                filteredEntities.forEach(cachedEntity => cachedEntity["$inFilter"] = true);
                self.$providerStorageService.FilteredEntities[providerId] = filteredEntities;
                return filteredEntities;
            });

        function updateParent(columns: string[], intermediaries: MetadataIntermediary[]): string[]
        {
            return columns.map(col =>
            {
                const intermediary = intermediaries.find(i => i.name === col);
                if (intermediary?.property["$parentPropertyName"])
                    return `${intermediary.property["$parentPropertyName"]}.${col}`;
                return col;
            });
        }
    }

    BuildGridRows(
        entities: any[],
        viewMetadata: IGridViewMetadata,
        serverPaging: boolean,
        multiselectActions?: IMetadataAction[],
        filteredColumns?: string[],
        includeLookups?: string[],
        forFilter?: boolean
    ): Promise<any[]>
    {
        if (!viewMetadata.SelectionMode)
            viewMetadata.SelectionMode = SelectionMode.None;
        if (entities.length === 0 || (filteredColumns && filteredColumns.every(x => x.indexOf(".") < 0)))
        {
            return this.loadRows(
                entities,
                viewMetadata,
                serverPaging,
                multiselectActions,
                filteredColumns,
                includeLookups,
                forFilter
            );
        }
        return this.$metadataTableService
            .AugmentModel(
                viewMetadata.GridProperties.ClassMetadata,
                entities,
                viewMetadata.SelectProperty ? viewMetadata.SelectProperty : "Selected"
            )
            .then(() =>
            {
                return this.LoadChildren(
                    entities,
                    viewMetadata,
                    serverPaging,
                    multiselectActions,
                    filteredColumns,
                    includeLookups,
                    forFilter
                );
            });
    }

    LoadChildren(
        entities: any[],
        viewMetadata: IGridViewMetadata,
        serverPaging: boolean,
        multiselectActions: IMetadataAction[],
        filteredColumns: string[],
        includeLookups?: string[],
        forFilter?: boolean
    ): Promise<any[]>
    {
        const self = this;
        const loaders: Promise<any>[] = [];
        const properties = propertiesToInclude();

        for (let i = 0; i < entities.length; i++)
        {
            const index = i;
            if (!entities[i]["$processed"])
            {
                loaders.push(
                    this.$metadataService
                        .VerifyLoadChildren(
                            properties,
                            viewMetadata.GridProperties.ClassMetadata.TypeName,
                            entities[i],
                            viewMetadata.SelectProperty ? viewMetadata.SelectProperty : "Selected"
                        )
                        .then(r => (entities[index] = r))
                );
            }
        }
        return Promise.all(loaders)
            .then(() =>
            {
                return self.loadRows(
                    entities,
                    viewMetadata,
                    serverPaging,
                    multiselectActions,
                    filteredColumns,
                    includeLookups,
                    forFilter
                );
            });

        function propertiesToInclude(): PropertyMetadata[]
        {
            let properties = viewMetadata.GridProperties.ClassMetadata.Properties.filter(x => !!x.ClassMetadata && x.ClassMetadata.Properties.length > 0 && !self.$metadataService.IsHiddenProperty(x));
            if (properties.length > 0 && filteredColumns?.length > 0)
                return properties.filter(p => filteredColumns.indexOf(`${p.Name}.${p.ClassMetadata.Properties[0].Name}`) > -1);
            return properties;
        }
    }

    loadRows(
        entities: any[],
        viewMetadata: IGridViewMetadata,
        serverPaging: boolean,
        multiselectActions: IMetadataAction[],
        filteredColumns: string[],
        includeLookups?: string[],
        forFilter?: boolean
    ): Promise<any[]>
    {
        const providerId = viewMetadata.GridProperties["$providerId"];
        const rows = entities
            .filter(x => (forFilter || !this.Scrolling[providerId] || !x["$processed"]) && (Utilities.IsNullOrUndefined(x.SerializedRowState) || x.SerializedRowState !== "Deleted"))
            .map((r, i) => this.createDataRow(r, i, viewMetadata, viewMetadata.SelectProperty, filteredColumns, includeLookups));

        if (Utilities.IsNullOrUndefined(filteredColumns))
        {
            if (this.Scrolling[providerId] && !Utilities.IsNullOrUndefined(this.$providerStorageService.GridData[providerId]))
                this.$providerStorageService.GridData[providerId].push(...rows);
            else
                this.$providerStorageService.GridData[providerId] = rows;

            const filteredList = entities.filter(
                x => Utilities.IsNullOrUndefined(x.SerializedRowState) || x.SerializedRowState !== "Deleted"
            );
            if (filteredList.length > 0 && (!forFilter || !viewMetadata["$columns"]))
                viewMetadata["$columns"] = this.defineColumns(
                    filteredList[0],
                    viewMetadata,
                    serverPaging
                );
            const actions = viewMetadata.Actions;
            if (!filteredColumns)
                viewMetadata["$selectOnly"] =
                    ((!actions || actions.length === 0) && viewMetadata.SelectionMode !== SelectionMode.None) ||
                    (actions?.length === 1 && multiselectActions[0].ReturnPath === "Multiselect");
        }

        if (includeLookups?.length === 0)
            return Promise.resolve(rows);
        return this.loadLookups(
            viewMetadata.GridProperties.ClassMetadata,
            rows,
            filteredColumns,
            includeLookups,
            entities
        );
    }

    defineColumns(entity: any, viewMetadata: IGridViewMetadata, serverPaging: boolean): any[]
    {
        const self = this;
        const metadata = viewMetadata.GridProperties.ClassMetadata;
        const intermediaries = this.$metadataService.FlattenProperties(metadata, entity.$$Parent, entity);
        return intermediaries
            .sort((a, b) => a.property.RenderPosition - b.property.RenderPosition)
            .map(x => createDataColumn(x));
        function createDataColumn(property: MetadataIntermediary): any
        {
            const propertyName = property.property.Name || property.property.Label.replace(/ /g, "");
            const column = {};
            column["Name"] = propertyName;
            if (property.property["$parentPropertyName"])
                column["ParentPropertyName"] = property.property["$parentPropertyName"];
            column["Label"] = self.$metadataService.EvaluateString(
                property.property.Label,
                property.model.$$Parent,
                property.model
            ).trim();
            column["DataType"] = self.$gridMetadataService.getDataType(property.property);
            column["IsCurrency"] = self.$gridMetadataService.isCurrency(property.property);
            column["IsBoolean"] = property.property.InputType === "Check";
            column["IsIcon"] = !Utilities.IsNullOrUndefined(property.property.ActionMetadata) && !Utilities.IsNullOrUndefined(property.property.ActionMetadata.Icon);
            column["IsMetadataAction"] = !Utilities.IsNullOrUndefined(property.property.ActionMetadata);
            column["IsLinkAction"] = property.property.LinkAction;
            column["DocumentLookupType"] = !Utilities.IsNullOrUndefined(property.property.Lookup) && property.property.Lookup.startsWith("DocumentTypeName") ? property.property.Lookup.split(':')[1] : null;
            column["IsFilterable"] = self.isFilterable(property.property, serverPaging);
            column["AlwaysHidden"] = property.property.DrawWidth === -2;
            if (!column["AlwaysHidden"] && property.property.DefaultPinned)
                column["DefaultPinned"] = property.property.DefaultPinned;
            column["WidthPercentage"] = property.property.DrawWidth === 100 || property.property.DrawWidth === -1 ? 0 : property.property.DrawWidth;
            column["HasTotal"] = property.property.SummaryType === MetadataSummary.Sum;
            column["Icon"] = Utilities.IsNullOrUndefined(property.property.ActionMetadata)
                ? ""
                : property.property.ActionMetadata.Icon;
            column["Visible"] = self.columnVisible(property) && property.property.DrawWidth !== -2;
            column["MetadataAction"] = Utilities.IsNullOrUndefined(property.property.ActionMetadata)
                ? undefined
                : property.property.ActionMetadata;
            column["LinkAction"] = property.property.LinkAction;
            if (property.property.Scale || property.property.Scale === 0)
                column["DigitsInfo"] = `1.${property.property.Scale}-${property.property.Scale}`;
            if (property.property.ScaleProperty)
            {
                const scale = self.$metadataService.DependentModuleValue(property.property.ScaleProperty, entity.$$Parent, entity);
                column["DigitsInfo"] = `1.${scale}-${scale}`;
            }
            return column;
        }
    }

    createDataRow(entity: any, index: number, viewMetadata: IGridViewMetadata, selectProperty: string, filteredColumns?: string[], includeLookups?: string[]): any
    {
        const self = this;
        const intermediaries = self.$metadataService.FlattenProperties(viewMetadata.GridProperties.ClassMetadata, entity.$$Parent, entity, filteredColumns);
        if (Utilities.IsNullOrUndefined(includeLookups) || includeLookups.length > 0)
            self.setPropertyIds(entity, intermediaries, viewMetadata.GridProperties.ClassMetadata, includeLookups);
        const item = {};
        intermediaries.sort((a, b) => a.property.RenderPosition - b.property.RenderPosition).forEach((x) => createDataColumn(x));
        if (!item["Id"])
            item["Id"] = self.$metadataTableService.GetId(viewMetadata.GridProperties.IdProperty, entity, index);
        item["Selected"] = setSelected(item["Id"]);
        if (!filteredColumns)
            entity["$processed"] = true;
        item["$$model"] = entity;
        if (entity["$addedCellEdit"])
            item["$cellEditAdded"] = true;
        item["Commands"] = rowCommands(viewMetadata.GridProperties, entity);
        if (viewMetadata.GridProperties.FeatureAction)
        {
            const action: IMetadataAction = Utilities.deepCopy(viewMetadata.GridProperties.FeatureAction);
            action.Caption = self.$metadataService.EvaluateString(action.Label, entity.$$Parent, entity);
            action.Hidden = self.$metadataPropertyService.isHidden(
                <PropertyMetadata>{
                    HiddenProperty: action.HiddenProperty,
                    Hidden: action.Hidden
                },
                entity,
                entity.$$Parent
            );
            action.Disabled = self.$metadataPropertyService.isDisabled(
                <PropertyMetadata>{
                    DisabledProperty: action.DisabledProperty,
                    Disabled: action.Disabled
                },
                entity,
                entity.$$Parent
            );
            item["FeatureAction"] = action;
        }

        function createDataColumn(intermediary: MetadataIntermediary): void
        {
            const propertyName = intermediary.property.Name || intermediary.property.Label.replace(/ /g, "");
            if (Utilities.IsNullOrUndefined(intermediary.property.ActionMetadata))
                item[propertyName] = setElementValue(intermediary);
            else
                item[propertyName] = Utilities.IsNullOrUndefined(intermediary.property.ActionMetadata.Label)
                    ? setElementValue(intermediary)
                    : intermediary.property.ActionMetadata.Label;
            if (Utilities.IsNullOrUndefined(item[propertyName]))
                item[propertyName] = intermediary.property.InputType === "Date" ? null : "";
        }
        function setElementValue(intermediary: MetadataIntermediary): string
        {
            if (intermediary.property.LookupProperty)
            {
                const list: KeyValuePair<MetadataKey, string>[] = self.$metadataService.DependentModuleValue(
                    intermediary.property.LookupProperty,
                    intermediary.parent,
                    intermediary.model
                );
                if (Utilities.IsNullOrUndefinedOrEmpty(list))
                    return "";
                return list.find(x => x.Key.toString() === intermediary.model[intermediary.name]?.toString())?.Value;
            }
            return self.$gridMetadataService.setValue(intermediary);
        }
        function rowCommands(gridMetadata: GridMetadata, model: any): IMetadataAction[]
        {
            const commands = [];
            if (gridMetadata.CanCopy)
            {
                commands.push(<IMetadataAction>{
                    Label: "Copy",
                    Caption: "Copy",
                });
            }
            if (Utilities.IsNullOrUndefined(gridMetadata.Actions)) return commands;
            gridMetadata.Actions.forEach(a =>
            {
                const addAction: IMetadataAction = Utilities.deepCopy(a);
                addAction.Caption = self.$metadataService.EvaluateString(a.Label, model.$$Parent, model);
                addAction.Hidden = self.$metadataPropertyService.isHidden(
                    <PropertyMetadata>{
                        HiddenProperty: a.HiddenProperty,
                        Hidden: a.Hidden
                    },
                    model,
                    model.$$Parent
                );
                addAction.Disabled = self.$metadataPropertyService.isDisabled(
                    <PropertyMetadata>{
                        DisabledProperty: a.DisabledProperty,
                        Disabled: a.Disabled
                    },
                    model,
                    model.$$Parent
                );
                commands.push(addAction);
            });
            return commands;
        }
        function setSelected(id: string): boolean
        {
            const providerId = viewMetadata.GridProperties["$providerId"];
            const selected = selectProperty ? entity[selectProperty] : entity["Selected"];
            if (!self.$gridSelectionService.SelectedIds[providerId])
                return selected;
            const index = self.$gridSelectionService.SelectedIds[providerId].indexOf(id);
            if (selected)
            {
                if (index < 0)
                    self.$gridSelectionService.AddSelected(providerId, id);
            }
            else
            {
                if (index > -1)
                    self.$gridSelectionService.RemoveSelected(providerId, index);
            }
            return selected;
        }

        return item;
    }

    columnVisible(intermediary: MetadataIntermediary): boolean
    {
        if (intermediary.property.Hidden) return false;
        if (!Utilities.IsNullOrUndefinedOrEmpty(intermediary.property.HiddenProperty))
            return !this.$metadataService.EvaluateBoolean(
                intermediary.property.HiddenProperty,
                intermediary.model["$$Parent"],
                intermediary.model
            );
        return true;
    }

    getPropertyPath(model: any, intermediary: MetadataIntermediary, parent: string = ""): string
    {
        let ret = "";
        Object.keys(model)
            .forEach(p =>
            {
                if (ret === "")
                {
                    if (model[p] === intermediary.model)
                    {
                        ret = Utilities.IsNullOrUndefinedOrEmpty(parent)
                            ? `${p}.${intermediary.property.Name}`
                            : `${parent}.${p}.${intermediary.property.Name}`;
                    }
                    else if (!!model[p] && typeof model[p] === "object" && !p.startsWith("$"))
                    {
                        ret = this.getPropertyPath(model[p], intermediary, p);
                    }
                }
            });
        return ret;
    }

    setPropertyIds(model: any, intermediaries: MetadataIntermediary[], metadata: ClassMetadata, includeLookups: string[]): void
    {
        const self = this;
        metadata.Properties.forEach(prop =>
        {
            if (!Utilities.IsNullOrUndefined(includeLookups) && includeLookups.indexOf(prop.Name) < 0)
                return;
            if (
                !Utilities.IsNullOrUndefined(model) &&
                !Utilities.IsNullOrUndefinedOrEmpty(prop.Lookup) &&
                !prop.Hidden &&
                !Utilities.IsNullOrUndefined(intermediaries.find(x => x.name === prop.Name))
            )
            {
                if (!Utilities.IsNullOrUndefined(model[prop.Name]))
                {
                    const ids = Array.isArray(<MetadataKey>model[prop.Name])
                        ? <MetadataKey[]>model[prop.Name]
                        : [<MetadataKey>model[prop.Name]];
                    if (Utilities.IsNullOrUndefined(self.lookups[prop.Name])) self.lookups[prop.Name] = [];
                    ids.forEach(id =>
                    {
                        if (this.lookups[prop.Name].indexOf(id) < 0) this.lookups[prop.Name].push(id);
                    });
                }
            }
            else if (!Utilities.IsNullOrUndefined(prop.ClassMetadata))
            {
                self.setPropertyIds(model[prop.Name], intermediaries, prop.ClassMetadata, includeLookups);
            }
        });
    }

    loadLookups(metadata: ClassMetadata, rows: any[], filteredColumns: string[], includeLookups: string[], entities: any[]): Promise<any[]>
    {
        const self = this;
        const promises = [];
        this.propertiesForCacheClear = self.$metadataService
            .FlattenProperties(metadata, null, {})
            .filter(
                x =>
                {
                    const isPropertyVisible = entities.some(entity => !self.$metadataPropertyService.isHidden(x.property, entity, entity.$$Parent));
                    const inFilter = isPropertyVisible &&
                        !Utilities.IsNullOrUndefinedOrEmpty(x.property.Lookup) &&
                        Utilities.IsNullOrUndefinedOrEmpty(x.property.LookupProperty);
                    if (filteredColumns)
                    {
                        filteredColumns = filteredColumns.map(x => x.substring(x.lastIndexOf(".") + 1));
                        return inFilter && filteredColumns.indexOf(x.property.Name) > -1;
                    }
                    if (!Utilities.IsNullOrUndefined(includeLookups) && includeLookups.indexOf(x.name) < 0)
                        return false;
                    return inFilter;
                }

            );
        this.propertiesForCacheClear.forEach(p =>
        {
            if (!p.property.Lookup.startsWith("DocumentTypeName"))
            {
                const promise = self.LookupValuesForTable(p.property, rows);
                promises.push(promise);
            }
        });
        return Promise.all(promises)
            .then(_r =>
            {
                return rows;
            });
    }

    LookupValueForTable(
        property: PropertyMetadata,
        rows: any[],
        ids: string[]
    ): Promise<void>
    {
        this.lookups[property.Name] = [];
        ids.forEach(id => this.lookups[property.Name].push(id));
        return this.LookupValuesForTable(property, rows);
    }

    LookupValuesForTable(property: PropertyMetadata, rows: any[], pagedLookup?: any[]): Promise<void>
    {
        const self = this;
        const filter = this.buildExistingFilter(property);
        // add something to this.$metadataInputSelectService that updates a property
        // what we really want is metadata-input-typeahead showSpinner to be true
        return this.CodeLookupGet(property.Lookup, pagedLookup ? pagedLookup : this.lookups[property.Name], filter)
            .then(
                (results: KeyValuePair<MetadataKey, string>[]) =>
                {
                    if (!self.lookupCache[property.Lookup + '|' + filter]) self.lookupCache[property.Lookup + '|' + filter] = [];
                    results = self.$metadataInputSelectService.MergeSelectOptions(property.Lookup, results, property);
                    results = self.MergeFreeEntryOptions(results, property);
                    if (!Utilities.IsNullOrUndefined(results))
                    {
                        results.forEach(r =>
                        {
                            if (self.lookupCache[property.Lookup + '|' + filter].indexOf(r) === -1)
                            {
                                self.lookupCache[property.Lookup + '|' + filter].push(r);
                            }
                        });
                    }
                    rows.forEach(row =>
                    {
                        const value = row[property.Name];
                        const result: KeyValuePair<MetadataKey, string>[] = [];
                        if (!Utilities.IsNullOrUndefined(results) || self.lookupCache[property.Lookup + '|' + filter].length > 0)
                        {
                            if (property.InputType === "MultiSelect")
                            {
                                const values = value?.toString()
                                    .split(",");
                                values.forEach(value =>
                                {
                                    const toPush = self.lookupCache[property.Lookup + '|' + filter].find(
                                        x =>
                                            x.Key.toString() === value.toString() || x.Value.toString() === value.toString()
                                    );
                                    if (toPush) result.push(toPush);
                                });
                            }
                            else
                            {
                                const toPush = self.lookupCache[property.Lookup + '|' + filter].find(
                                    x => x.Key.toString() === value?.toString() || x.Value?.toString() === value?.toString()
                                );
                                if (toPush) result.push(toPush);
                            }
                        }
                        row[property.Name] = result.length > 0
                            ? result.map(x => x.Value).join(",")
                            : "";
                    });
                },
                () =>
                    rows.forEach(row =>
                    {
                        row[property.Name] = "Error Loading";
                    })
            );
    }

    MergeFreeEntryOptions(
        results: KeyValuePair<MetadataKey, string>[],
        propertyMetadata: PropertyMetadata
    ): KeyValuePair<MetadataKey, string>[]
    {
        if (!propertyMetadata.FreeEntry || Utilities.IsNullOrUndefined(this.lookups[propertyMetadata.Name]))
            return results;

        this.lookups[propertyMetadata.Name].forEach(x =>
        {
            const kvp = <KeyValuePair<MetadataKey, string>>{
                Key: x,
                Value: x
            };
            if (x !== 0 && x !== "" && results.indexOf(kvp) < 0)
                results.push(kvp);
        });
        return results;
    }

    buildExistingFilter(property: PropertyMetadata): string
    {
        if (!Utilities.IsNullOrUndefined(property?.ConstantFilter) && !property.ConstantFilter.match("[{}]"))
            return `(${property.ConstantFilter})`;
    }

    isKeyEmpty(id: MetadataKey): boolean
    {
        return MetadataKeyHelpers.isNullKey(id) || (MetadataKeyHelpers.isStringKey(id) && id === "");
    }

    CodeLookupGet(
        lookup: string,
        ids: MetadataKey[],
        existingFilter: string
    ): Promise<KeyValuePair<MetadataKey, string>[]>
    {
        const newIds = [];
        const self = this;
        if (!Utilities.IsNullOrUndefined(ids))
        {
            ids.filter(i => !this.isKeyEmpty(i))
                .forEach(id =>
                {
                    if (!self.lookupCache[lookup + '|' + existingFilter] || self.lookupCache[lookup + '|' + existingFilter].filter(k => k.Key === id).length === 0)
                    {
                        newIds.push(id);
                    }
                });
        }
        if (!Utilities.IsNullOrUndefined(newIds) && newIds.length > 0)
        {
            let batchLength = 100;
            if (newIds[0].toString().length > 10)
                batchLength = 40;
            const loaders: Promise<any>[] = [];
            for (let i = 0; i < newIds.length; i = i + batchLength)
            {
                const builder = this.$odataFilterFactory
                    .createBuilder()
                    .in("Key", newIds.slice(i, Math.min(i + batchLength, newIds.length)));
                if (!Utilities.IsNullOrUndefinedOrEmpty(existingFilter)) builder.constantFilter(existingFilter);
                loaders.push(this.$cachedCodeLookupService.Get(lookup, builder.build()));
            }
            return Promise.all(loaders)
                .then(results =>
                {
                    return Array.prototype.concat.apply([], results);
                });
        }
        return Promise.resolve(self.lookupCache[lookup + '|' + existingFilter]);
    }



    isFilterable(property: PropertyMetadata, serverPaging: boolean): boolean
    {
        if (!serverPaging) return true;
        return !property.UnMapped;
    }

    allowShowRecent(viewMetadata: IGridViewMetadata): boolean
    {
        return !viewMetadata.SubPropertyName && viewMetadata.GridProperties.IdProperty && this.$metadataFormService.initialForm;
    }

    hasRecent(gridId: string): boolean
    {
        return this.recentIds(gridId).length > 0;
    }

    recentIds(gridId: string): string[]
    {
        const setting = this.getRecentSetting();
        if (setting && setting[gridId])
            return setting[gridId];
        return [];
    }

    addToRecentIds(gridId: string, rowId: string): void
    {
        let setting = this.getRecentSetting();
        if (!setting)
            setting = {};
        if (!setting[gridId])
            setting[gridId] = [rowId];
        else if (setting[gridId].indexOf(rowId) < 0)
            setting[gridId].push(rowId);
        this.$storageService.Set(recentSearchesKey, JSON.stringify(setting));
    }

    private getRecentSetting(): Record<string, string[]>
    {
        let stringSetting = this.$storageService.Get<string>(recentSearchesKey);
        if (!stringSetting)
            stringSetting = null;
        return JSON.parse(stringSetting);
    }

    private readonly modelChanged$ = new Subject<string>();

    get onModelChanged(): Observable<string>
    {
        return this.modelChanged$.asObservable();
    }

    fireModelChangedListeners(providerId: string): void
    {
        this.modelChanged$.next(providerId);
    }
}
